import React, { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, FileSignature, GitCompare, Lock, Send, ShieldAlert, UserCheck } from "lucide-react";
import api, { apiError, ruleReasons } from "../lib/api";
import { userName, useUsers } from "../lib/hooks";
import { Chip, PageHeader, ReasonsList, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { MINUTE_STATUS, OBJECTION_STATUS, RECIPIENT_STATUS, SIG_STATUS, fmtDateTime } from "../lib/labels";
import { useAuth } from "../context/AuthContext";

const CONF = { interna: "Interna", proyecto: "Proyecto", confidencial: "Confidencial", publica: "Distribuible" };
const CHANNELS = { outlook: "Outlook (mock)", teams: "Teams (mock)", sharepoint: "SharePoint (mock)", descarga: "Descarga", portal: "Portal de participante" };
const ACTION_LABELS = {
  enviar_revision: "Enviar a revisión", solicitar_cambios: "Solicitar cambios",
  preparar_aprobacion: "Preparar para aprobación", solicitar_aprobacion: "Solicitar aprobación",
  distribuir: "Distribuir", marcar_requiere_info: "Requiere información",
  marcar_con_observaciones: "Con observaciones", revisar_observaciones: "Revisar observaciones",
  archivar: "Archivar acta",
};
const SECTION_OPS = [
  ["editar", "Editar"], ["regenerar", "Regenerar"], ["restaurar", "Restaurar"], ["bloquear", "Bloquear"],
  ["desbloquear", "Desbloquear"], ["comentar", "Comentar"], ["marcar_incorrecto", "Incorrecta"],
  ["aprobar_seccion", "Aprobar sección"], ["verificar_fuente", "Verificar fuentes"],
];

function srcLabel(s) {
  const t = { candidate: "Candidato validado", agenda_item: "Punto de agenda", reunion: "Reunión", proyecto: "Economía del proyecto", minute: "Acta anterior", riesgo: "Riesgo" }[s.entity_type] || s.entity_type;
  return `${t} ${s.entity_id}${s.timestamp ? ` · t=${s.timestamp}` : ""}${s.speaker ? ` · ${s.speaker}` : ""}`;
}

export default function ReunionActa() {
  const { mid } = useParams();
  const users = useUsers();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null); // {kind: action|seccion|objecion|firma|destinatario|diff, ...}
  const [fields, setFields] = useState({});
  const [err, setErr] = useState(null);
  const [verSel, setVerSel] = useState(null);

  const { data, error } = useQuery({ queryKey: ["acta", mid], queryFn: async () => (await api.get(`/reuniones/${mid}/acta`)).data, retry: false });
  const { data: meeting } = useQuery({ queryKey: ["meeting", mid], queryFn: async () => (await api.get(`/reuniones/${mid}`)).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["acta", mid] }); qc.invalidateQueries({ queryKey: ["meeting", mid] }); };
  const mut = useMutation({
    mutationFn: async ({ path, body }) => (await api.post(path, body || {})).data,
    onSuccess: () => { toast.success("Operación aplicada"); setDialog(null); setFields({}); setErr(null); refresh(); },
    onError: (e) => { setErr(e); toast.error(apiError(e) || "Error al aplicar la operación"); },
  });

  const minute = data?.minute;
  const actions = minute?.actions || [];
  const allowed = actions.filter((a) => a.allowed);
  const blockedNext = actions.filter((a) => !a.allowed && a.permission && (a.reasons || []).length && !(a.reasons || []).some((r) => r.code === "estado" || r.code === "permiso"));
  const editable = minute && !["aprobada", "distribuida", "con_observaciones", "revisada", "archivada", "sustituida"].includes(minute.status);
  const pfFails = (data?.preflight || []).filter((c) => !c.ok);
  const dist = data?.distribution;
  const sig = data?.signature;
  const openSections = (data?.objections || []).filter((o) => ["recibida", "en_revision", "escalada"].includes(o.status));

  const act = (action, extra = {}) => mut.mutate({ path: `/reuniones/${mid}/acta/accion`, body: { action, ...fields, ...extra } });
  const openDlg = (d) => { setDialog(d); setFields({}); setErr(null); };

  const dialogNeeds = useMemo(() => {
    if (!dialog || dialog.kind !== "action") return [];
    return (actions.find((a) => a.action === dialog.action)?.needs) || [];
  }, [dialog, actions]);

  if (error) {
    return (
      <div className="p-8" data-testid="acta-denied">
        <p className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Acceso restringido: el acta interna (con notas y comentarios de revisión) no está disponible para tu usuario. Si eres destinatario de la distribución, usa el portal de participante. {apiError(error)}
        </p>
      </div>
    );
  }
  if (!data) return <div className="p-8 text-sm text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="acta-page">
        <Link to="/reuniones" className="flex items-center gap-1 text-xs text-blue-700 hover:underline" data-testid="back-to-meetings">
        <ArrowLeft className="h-3 w-3" /> Volver a reuniones
      </Link>
      <Link to={`/reuniones/${mid}/seguimiento`} className="text-xs font-medium text-blue-700 hover:underline" data-testid="open-seguimiento">Seguimiento →</Link>
      <PageHeader
        title={`Acta — ${meeting?.title || mid}`}
        subtitle="El acta se construye solo desde fuentes validadas. Ninguna versión aprobada o distribuida es editable: las correcciones crean una versión nueva."
        testid="acta-title"
        actions={minute && (
          <div className="flex items-center gap-2">
            <Chip map={MINUTE_STATUS} value={minute.status} testid="minute-status" />
            {data.version && <ToneChip label={data.version.label} tone={data.version.approved ? "success" : "muted"} testid="minute-version-label" />}
            {minute.confidentiality && <ToneChip label={CONF[minute.confidentiality]} tone="info" testid="minute-confidentiality" />}
            {minute.signed && <ToneChip label="Firmada" tone="success" testid="minute-signed" />}
          </div>
        )}
      />

      {/* Generación */}
      {!minute && (
        <div className="rounded-md border border-slate-200 bg-white p-6" data-testid="no-minute">
          <p className="text-sm text-slate-600">Esta reunión aún no tiene acta. El borrador se genera solo si la reunión está celebrada y, si hay transcript, su validación humana está cerrada (TRANSCRIPT ≠ ACTA).</p>
          {data.can_generate && (
            <Button className="mt-3" onClick={() => mut.mutate({ path: `/reuniones/${mid}/acta/generar` })} disabled={mut.isPending} data-testid="generate-draft">
              Generar borrador desde fuentes validadas
            </Button>
          )}
        </div>
      )}

      {minute && (
        <>
          {/* Qué falta: Motivo | Responsable | Acción */}
          {pfFails.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid="preflight-panel">
              <p className="text-sm font-semibold text-amber-900"><ShieldAlert className="mr-1 inline h-4 w-4" />Preflight — {pfFails.length} controles pendientes antes de aprobar</p>
              <div className="mt-2 space-y-1">
                {pfFails.map((c) => (
                  <div key={c.check} className="grid gap-1 rounded border border-amber-100 bg-white px-2 py-1.5 text-xs sm:grid-cols-3" data-testid={`preflight-${c.check}`}>
                    <span className="font-medium text-slate-800">{c.label}</span>
                    <span className="text-slate-500">Responsable: <strong>{userName(users.map, c.responsible_id)}</strong></span>
                    <span className="text-amber-700">{c.action}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Siguiente acción válida (contextual) */}
          <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="next-actions">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Siguiente acción válida</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {allowed.filter((a) => ACTION_LABELS[a.action]).map((a) => (
                <Button key={a.action} size="sm" variant={["distribuir", "solicitar_aprobacion", "archivar"].includes(a.action) ? "default" : "outline"}
                  data-testid={`action-${a.action}`} onClick={() => openDlg({ kind: "action", action: a.action })}>
                  {a.action === "distribuir" && <Send className="mr-1 h-3.5 w-3.5" />}
                  {ACTION_LABELS[a.action]}
                </Button>
              ))}
              {!allowed.length && <p className="text-sm text-slate-400">No tienes acciones disponibles en este estado o con tus permisos.</p>}
            </div>
            {blockedNext.map((a) => (
              <div key={a.action} className="mt-2" data-testid={`blocked-${a.action}`}>
                <p className="mb-1 text-xs font-medium text-red-700">{ACTION_LABELS[a.action] || a.action} bloqueada:</p>
                <ReasonsList reasons={a.reasons} />
              </div>
            ))}
          </div>

          {(data.closure_blockers || []).length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="closure-blockers">
              <p className="font-semibold">Bloqueos de cierre de la reunión:</p>
              {(data.closure_blockers || []).map((b, i) => <p key={i}>· {b.message || b.code}</p>)}
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Secciones */}
            <div className="space-y-3 lg:col-span-2" data-testid="sections-pane">
              {(data.version?.sections || []).map((s) => (
                <div key={s.key} className={`rounded-md border bg-white p-3 ${s.material_without_source ? "border-red-300" : s.pending_info ? "border-amber-200" : "border-slate-200"}`} data-testid={`section-${s.key}`}>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="text-sm font-semibold text-slate-800">{s.title}</p>
                    {s.required && <ToneChip label="obligatoria" tone="muted" />}
                    {s.ai_generated && <ToneChip label="borrador IA" tone="ai" testid={`section-ai-${s.key}`} />}
                    {s.locked && <ToneChip label="bloqueada" tone="danger" testid={`section-locked-${s.key}`} />}
                    {s.pending_info && <ToneChip label="información pendiente" tone="warning" testid={`section-pending-${s.key}`} />}
                    {s.material_without_source && <ToneChip label="material sin fuente" tone="danger" testid={`section-nosource-${s.key}`} />}
                    {s.marked_incorrect && <ToneChip label="marcada incorrecta" tone="danger" />}
                    {s.review_status === "aprobada" && <ToneChip label="sección aprobada" tone="success" testid={`section-ok-${s.key}`} />}
                  </div>
                  <p className={`mt-1.5 whitespace-pre-line text-sm ${s.pending_info ? "italic text-amber-700" : "text-slate-700"}`} data-testid={`section-content-${s.key}`}>{s.content}</p>
                  {(s.sources || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1" data-testid={`section-sources-${s.key}`}>
                      {s.sources.map((src, i) => <ToneChip key={i} label={srcLabel(src)} tone="info" testid={`source-${s.key}-${i}`} />)}
                      {s.source_verified_by && <ToneChip label={`fuentes verificadas por ${userName(users.map, s.source_verified_by)}`} tone="success" />}
                    </div>
                  )}
                  {(s.comments || []).map((c, i) => (
                    <p key={i} className="mt-1 rounded bg-slate-50 px-2 py-1 text-xs text-slate-500" data-testid={`section-comment-${s.key}-${i}`}>
                      {userName(users.map, c.by)}: {c.text}
                    </p>
                  ))}
                  {editable && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {SECTION_OPS.filter(([op]) => !(op === "desbloquear" && !s.locked) && !(op === "bloquear" && s.locked)).map(([op, label]) => (
                        <Button key={op} size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`sec-${op}-${s.key}`}
                          onClick={() => ["regenerar", "bloquear", "desbloquear", "aprobar_seccion", "verificar_fuente"].includes(op)
                            ? mut.mutate({ path: `/reuniones/${mid}/acta/seccion/${s.key}`, body: { op } })
                            : openDlg({ kind: "seccion", op, section: s })}>
                          {label}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Columna lateral */}
            <div className="space-y-3">
              {/* Versiones + diff */}
              <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="versions-panel">
                <p className="text-sm font-semibold text-slate-800"><GitCompare className="mr-1 inline h-3.5 w-3.5" />Versiones (inmutables)</p>
                {(data.versions || []).map((v) => (
                  <div key={v.id} className="mt-1.5 rounded border border-slate-100 px-2 py-1.5 text-xs" data-testid={`version-${v.label}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{v.label} {v.approved ? "· aprobada" : v.distributed ? "· distribuida" : ""}</span>
                      <button className="text-blue-700 underline" data-testid={`version-diff-${v.label}`} onClick={() => openDlg({ kind: "diff", version: v })}>diff</button>
                    </div>
                    <p className="text-slate-500">{fmtDateTime(v.created_at)} · {v.change_reason}</p>
                  </div>
                ))}
              </div>

              {/* Distribución */}
              <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="distribution-panel">
                <p className="text-sm font-semibold text-slate-800"><Send className="mr-1 inline h-3.5 w-3.5" />Distribución</p>
                {!dist ? <p className="mt-1 text-xs text-slate-400">Sin distribución. Solo una versión aprobada puede distribuirse, con confirmación humana.</p> : (
                  <>
                    <p className="mt-1 text-xs text-slate-500" data-testid="dist-meta">{dist.version_label} · enviada {fmtDateTime(dist.sent_at)} por {userName(users.map, dist.sent_by)} · observaciones hasta {fmtDateTime(dist.observations_deadline)}</p>
                    {dist.recipients.map((r) => (
                      <div key={r.id} className="mt-1.5 rounded border border-slate-100 px-2 py-1.5 text-xs" data-testid={`recipient-${r.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{r.name}{r.external && !String(r.name).includes("(externo)") ? " (externo)" : ""} · {CHANNELS[r.channel]}</span>
                          <Chip map={RECIPIENT_STATUS} value={r.status} testid={`recipient-status-${r.id}`} />
                        </div>
                        {r.error && <p className="text-red-600">{r.error}</p>}
                        {(r.acknowledgements || []).length > 0 && <p className="text-slate-400">acks: {r.acknowledgements.map((a) => a.kind).join(", ")}</p>}
                        <div className="mt-1 flex flex-wrap gap-1">
                          {r.user_id === user?.id && ["enviado", "entregado", "abierto", "descargado"].includes(r.status) && (
                            <>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`ack-open-${r.id}`} onClick={() => mut.mutate({ path: `/reuniones/${mid}/acta/destinatario/${r.id}/evento`, body: { event: "abierto" } })}>Abrir</Button>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`ack-confirm-${r.id}`} onClick={() => mut.mutate({ path: `/reuniones/${mid}/acta/destinatario/${r.id}/evento`, body: { event: "confirmado" } })}>Confirmar (ack)</Button>
                            </>
                          )}
                          {["rebotado", "error", "pendiente"].includes(r.status) && allowed.length > 0 && (
                            <>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`fix-email-${r.id}`} onClick={() => openDlg({ kind: "destinatario", op: "corregir_email", recipient: r })}>Corregir email</Button>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`resend-${r.id}`} onClick={() => mut.mutate({ path: `/reuniones/${mid}/acta/destinatario/${r.id}/evento`, body: { event: "reenviar" } })}>Reenviar</Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Objeciones */}
              <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="objections-panel">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-800"><ShieldAlert className="mr-1 inline h-3.5 w-3.5" />Objeciones ({openSections.length} abiertas)</p>
                  {["aprobada", "distribuida", "con_observaciones", "revisada"].includes(minute.status) && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="objection-new" onClick={() => openDlg({ kind: "objecion" })}>Nueva</Button>
                  )}
                </div>
                {(data.objections || []).map((o) => (
                  <div key={o.id} className="mt-1.5 rounded border border-slate-100 px-2 py-1.5 text-xs" data-testid={`objection-${o.id}`}>
                    <div className="flex items-center gap-1.5">
                      <Chip map={OBJECTION_STATUS} value={o.status} testid={`objection-status-${o.id}`} />
                      {o.material && <ToneChip label="MATERIAL — bloquea cierre" tone="danger" testid={`objection-material-${o.id}`} />}
                    </div>
                    <p className="mt-1 text-slate-700">[{o.section_key}] {o.text}</p>
                    <p className="text-slate-400">por {userName(users.map, o.created_by)} · {o.motivo}</p>
                    {o.resolution && <p className="text-emerald-700">Resolución: {o.resolution.motivo}{o.resolution.label ? ` → ${o.resolution.label}` : ""}</p>}
                    <div className="mt-1 flex flex-wrap gap-1">
                      {o.status === "recibida" && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`obj-revisar-${o.id}`} onClick={() => mut.mutate({ path: `/reuniones/${mid}/acta/objeciones/${o.id}/accion`, body: { action: "revisar" } })}>Revisar</Button>}
                      {o.status === "en_revision" && (
                        <>
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`obj-aceptar-${o.id}`} onClick={() => openDlg({ kind: "obj_accion", action: "aceptar", obj: o })}>Aceptar</Button>
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`obj-rechazar-${o.id}`} onClick={() => openDlg({ kind: "obj_accion", action: "rechazar", obj: o })}>Rechazar</Button>
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`obj-escalar-${o.id}`} onClick={() => openDlg({ kind: "obj_accion", action: "escalar", obj: o })}>Escalar</Button>
                        </>
                      )}
                      {o.status === "aceptada" && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`obj-incorporar-${o.id}`} onClick={() => openDlg({ kind: "obj_accion", action: "incorporar", obj: o })}>Incorporar (nueva versión)</Button>}
                      {["incorporada", "escalada", "rechazada"].includes(o.status) && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`obj-resolver-${o.id}`} onClick={() => openDlg({ kind: "obj_accion", action: "resolver", obj: o })}>Resolver</Button>}
                    </div>
                  </div>
                ))}
                {!(data.objections || []).length && <p className="mt-1 text-xs text-slate-400">Sin objeciones registradas. El silencio no es aceptación: los acuses son explícitos.</p>}
              </div>

              {/* Firma */}
              <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="signature-panel">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-800"><FileSignature className="mr-1 inline h-3.5 w-3.5" />Firma {minute.requires_signature ? "(requerida)" : "(opcional)"}</p>
                  {["aprobada", "distribuida", "con_observaciones"].includes(minute.status) && !minute.signed && !sig && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="signature-prepare" onClick={() => openDlg({ kind: "firma" })}>Preparar</Button>
                  )}
                </div>
                {!sig ? <p className="mt-1 text-xs text-slate-400">Signature Adapter (DocuSign mock) — solo sobre la versión aprobada, fijada por ID de versión.</p> : (
                  <div className="mt-1 text-xs" data-testid="signature-status-block">
                    <div className="flex items-center gap-2">
                      <Chip map={SIG_STATUS} value={sig.status} testid="signature-status" />
                      <span className="text-slate-500">{sig.adapter} · doc {sig.document_version}</span>
                    </div>
                    {sig.signers.map((s) => (
                      <div key={s.email} className="mt-1 flex items-center justify-between rounded border border-slate-100 px-2 py-1" data-testid={`signer-${s.email}`}>
                        <span>{s.order}. {s.name} {s.signed_at ? `· firmado ${fmtDateTime(s.signed_at)}` : ""}</span>
                        {sig.status === "enviado" && s.status !== "firmado" && (
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`sign-${s.email}`}
                            onClick={() => mut.mutate({ path: `/reuniones/${mid}/acta/firma/${sig.id}/evento`, body: { event: "firmar", signer_email: s.email } })}>Firmar (demo)</Button>
                        )}
                      </div>
                    ))}
                    {sig.status === "preparado" && (
                      <Button size="sm" className="mt-2 h-7 text-xs" data-testid="signature-send"
                        onClick={() => mut.mutate({ path: `/reuniones/${mid}/acta/firma/${sig.id}/evento`, body: { event: "enviar", confirm: true } })}>Enviar a firma</Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Diálogo genérico de acciones de workflow */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="acta-dialog">
          <DialogHeader><DialogTitle>
            {dialog?.kind === "action" && (ACTION_LABELS[dialog.action] || dialog.action)}
            {dialog?.kind === "seccion" && `${dialog.op} — ${dialog.section?.title}`}
            {dialog?.kind === "objecion" && "Nueva objeción sobre el acta"}
            {dialog?.kind === "obj_accion" && `Objeción: ${dialog.action}`}
            {dialog?.kind === "firma" && "Preparar firma del acta (DocuSign mock)"}
            {dialog?.kind === "destinatario" && "Corregir email del destinatario"}
            {dialog?.kind === "diff" && `Diff ${dialog.version?.label} vs versión anterior`}
          </DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-3">
              {dialog.kind === "diff" && (
                <div data-testid="diff-view">
                  <p className="text-xs text-slate-500">Motivo del cambio: {dialog.version.change_reason}</p>
                  {(dialog.version.diff || []).length ? dialog.version.diff.map((d, i) => (
                    <p key={i} className="mt-1 rounded bg-slate-50 px-2 py-1 text-xs" data-testid={`diff-entry-${i}`}>
                      Sección <strong>{d.section}</strong>: {d.change}
                    </p>
                  )) : <p className="mt-1 text-xs text-slate-400">Sin cambios respecto a la versión anterior.</p>}
                </div>
              )}
              {dialog.kind === "action" && dialogNeeds.includes("comment") && (
                <div><Label>Comentario *</Label><Textarea value={fields.comment || ""} onChange={(e) => setFields({ ...fields, comment: e.target.value })} data-testid="field-comment" /></div>
              )}
              {dialog.kind === "action" && dialogNeeds.includes("motivo") && (
                <div><Label>Motivo *</Label><Textarea value={fields.motivo || ""} onChange={(e) => setFields({ ...fields, motivo: e.target.value })} data-testid="field-motivo" /></div>
              )}
              {dialog.kind === "action" && dialogNeeds.includes("section_key") && (
                <div><Label>Sección *</Label>
                  <Select value={fields.section_key || ""} onValueChange={(v) => setFields({ ...fields, section_key: v })}>
                    <SelectTrigger data-testid="field-section_key"><SelectValue placeholder="Sección" /></SelectTrigger>
                    <SelectContent>{(data.version?.sections || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {dialog.kind === "action" && dialogNeeds.includes("cambio") && (
                <div><Label>Cambio solicitado *</Label><Textarea value={fields.cambio || ""} onChange={(e) => setFields({ ...fields, cambio: e.target.value })} data-testid="field-cambio" /></div>
              )}
              {dialog.kind === "action" && dialogNeeds.includes("responsible_id") && (
                <div><Label>Responsable de aplicar el cambio *</Label>
                  <Select value={fields.responsible_id || ""} onValueChange={(v) => setFields({ ...fields, responsible_id: v })}>
                    <SelectTrigger data-testid="field-responsible_id"><SelectValue placeholder="Usuario" /></SelectTrigger>
                    <SelectContent>{(users.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {dialog.kind === "action" && dialogNeeds.includes("confidentiality") && (
                <>
                  <div><Label>Confidencialidad *</Label>
                    <Select value={fields.confidentiality || ""} onValueChange={(v) => setFields({ ...fields, confidentiality: v })}>
                      <SelectTrigger data-testid="field-confidentiality"><SelectValue placeholder="Nivel" /></SelectTrigger>
                      <SelectContent>{Object.entries(CONF).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Aprobador adicional (si el tipo lo requiere)</Label>
                    <Select value={fields.approver_extra_id || ""} onValueChange={(v) => setFields({ ...fields, approver_extra_id: v })}>
                      <SelectTrigger data-testid="field-approver_extra_id"><SelectValue placeholder="Opcional" /></SelectTrigger>
                      <SelectContent>{(users.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {dialog.kind === "action" && dialogNeeds.includes("mensaje") && (
                <>
                  <div><Label>Mensaje de distribución *</Label><Textarea value={fields.mensaje || ""} onChange={(e) => setFields({ ...fields, mensaje: e.target.value })} data-testid="field-mensaje" /></div>
                  <div>
                    <Label>Destinatarios internos *</Label>
                    <div className="max-h-40 space-y-1 overflow-y-auto rounded border border-slate-200 p-2" data-testid="recipient-picker">
                      {(users.users || []).map((u) => {
                        const on = (fields.recipients || []).some((r) => r.user_id === u.id);
                        return (
                          <label key={u.id} className="flex items-center gap-2 text-xs" data-testid={`recipient-opt-${u.id}`}>
                            <input type="checkbox" checked={on} onChange={() => {
                              const cur = fields.recipients || [];
                              setFields({ ...fields, recipients: on ? cur.filter((r) => r.user_id !== u.id) : [...cur, { user_id: u.id, email: u.email, channel: "outlook" }] });
                            }} />
                            {u.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Externo (nombre)</Label><Input value={fields.ext_name || ""} onChange={(e) => setFields({ ...fields, ext_name: e.target.value })} data-testid="field-ext_name" /></div>
                    <div><Label>Externo (email)</Label><Input value={fields.ext_email || ""} onChange={(e) => setFields({ ...fields, ext_email: e.target.value })} placeholder="usa bounce@… para simular rebote" data-testid="field-ext_email" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-sm" data-testid="field-confirm-wrap">
                    <input type="checkbox" checked={!!fields.confirm} onChange={(e) => setFields({ ...fields, confirm: e.target.checked })} data-testid="field-confirm" />
                    Confirmo la distribución de la versión aprobada (acción humana obligatoria)
                  </label>
                </>
              )}
              {dialog.kind === "seccion" && dialog.op === "editar" && (
                <div><Label>Contenido *</Label><Textarea rows={6} value={fields.content ?? dialog.section.content} onChange={(e) => setFields({ ...fields, content: e.target.value })} data-testid="field-content" /></div>
              )}
              {dialog.kind === "seccion" && ["comentar", "marcar_incorrecto"].includes(dialog.op) && (
                <div><Label>Comentario *</Label><Textarea value={fields.comment || ""} onChange={(e) => setFields({ ...fields, comment: e.target.value })} data-testid="field-comment" /></div>
              )}
              {dialog.kind === "seccion" && dialog.op === "restaurar" && (
                <p className="text-xs text-slate-500">Restaura el contenido de esta sección desde la versión anterior. Se crea una versión nueva con diff.</p>
              )}
              {dialog.kind === "objecion" && (
                <>
                  <div><Label>Sección *</Label>
                    <Select value={fields.section_key || ""} onValueChange={(v) => setFields({ ...fields, section_key: v })}>
                      <SelectTrigger data-testid="field-section_key"><SelectValue placeholder="Sección" /></SelectTrigger>
                      <SelectContent>{(data.version?.sections || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Objeción *</Label><Textarea value={fields.text || ""} onChange={(e) => setFields({ ...fields, text: e.target.value })} data-testid="field-text" /></div>
                  <div><Label>Motivo *</Label><Textarea value={fields.motivo || ""} onChange={(e) => setFields({ ...fields, motivo: e.target.value })} data-testid="field-motivo" /></div>
                  <div><Label>Propuesta de corrección</Label><Textarea value={fields.propuesta || ""} onChange={(e) => setFields({ ...fields, propuesta: e.target.value })} data-testid="field-propuesta" /></div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!fields.material} onChange={(e) => setFields({ ...fields, material: e.target.checked })} data-testid="field-material" /> Objeción material (bloquea el cierre)</label>
                </>
              )}
              {dialog.kind === "obj_accion" && (
                <>
                  <div><Label>{dialog.action === "rechazar" || dialog.action === "escalar" ? "Motivo *" : "Comentario / motivo *"}</Label>
                    <Textarea value={fields.motivo || fields.comment || ""} onChange={(e) => setFields({ ...fields, motivo: e.target.value, comment: e.target.value })} data-testid="field-motivo" /></div>
                  {dialog.action === "incorporar" && (
                    <>
                      <div><Label>Contenido corregido de la sección (opcional; por defecto la propuesta)</Label>
                        <Textarea rows={4} value={fields.content || ""} onChange={(e) => setFields({ ...fields, content: e.target.value })} data-testid="field-content" /></div>
                      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!fields.source_reviewed} onChange={(e) => setFields({ ...fields, source_reviewed: e.target.checked })} data-testid="field-source_reviewed" /> Fuente revisada</label>
                    </>
                  )}
                </>
              )}
              {dialog.kind === "firma" && (
                <>
                  <p className="text-xs text-slate-500">Firmantes en orden. La solicitud queda fijada a la versión {data.version?.label} (ID {data.version?.id}).</p>
                  {(fields.signers || [{ name: "", email: "" }]).map((s, i) => (
                    <div key={i} className="grid grid-cols-2 gap-2">
                      <Input placeholder="Nombre" value={s.name} data-testid={`field-signer-name-${i}`}
                        onChange={(e) => { const sg = [...(fields.signers || [{ name: "", email: "" }])]; sg[i] = { ...sg[i], name: e.target.value }; setFields({ ...fields, signers: sg }); }} />
                      <Input placeholder="Email" value={s.email} data-testid={`field-signer-email-${i}`}
                        onChange={(e) => { const sg = [...(fields.signers || [{ name: "", email: "" }])]; sg[i] = { ...sg[i], email: e.target.value }; setFields({ ...fields, signers: sg }); }} />
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="signer-add"
                    onClick={() => setFields({ ...fields, signers: [...(fields.signers || [{ name: "", email: "" }]), { name: "", email: "" }] })}>+ firmante</Button>
                </>
              )}
              {dialog.kind === "destinatario" && (
                <div><Label>Email corregido *</Label><Input value={fields.email || ""} onChange={(e) => setFields({ ...fields, email: e.target.value })} data-testid="field-email" /></div>
              )}
              {err && (ruleReasons(err) ? <ReasonsList reasons={ruleReasons(err)} /> : <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="acta-error">{apiError(err)}</p>)}
              {dialog.kind !== "diff" && (
                <Button data-testid="acta-dialog-submit" disabled={mut.isPending}
                  onClick={() => {
                    const f = { ...fields };
                    if (dialog.kind === "action" && dialog.action === "distribuir") {
                      f.recipients = [...(f.recipients || [])];
                      if (f.ext_email) f.recipients.push({ name: f.ext_name || f.ext_email, email: f.ext_email, external: true, channel: "outlook" });
                      delete f.ext_name; delete f.ext_email;
                    }
                    if (dialog.kind === "action") act(dialog.action);
                    else if (dialog.kind === "seccion") mut.mutate({ path: `/reuniones/${mid}/acta/seccion/${dialog.section.key}`, body: { op: dialog.op, ...f } });
                    else if (dialog.kind === "objecion") mut.mutate({ path: `/reuniones/${mid}/acta/objeciones`, body: f });
                    else if (dialog.kind === "obj_accion") mut.mutate({ path: `/reuniones/${mid}/acta/objeciones/${dialog.obj.id}/accion`, body: { action: dialog.action, ...f } });
                    else if (dialog.kind === "firma") mut.mutate({ path: `/reuniones/${mid}/acta/firma/preparar`, body: { signers: (f.signers || [{ name: "", email: "" }]).filter((s) => s.name && s.email) } });
                    else if (dialog.kind === "destinatario") mut.mutate({ path: `/reuniones/${mid}/acta/destinatario/${dialog.recipient.id}/evento`, body: { event: "corregir_email", email: f.email } });
                  }}>
                  Confirmar
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
